/**
 * Deterministic analyst-support derivations for a v3 anomaly case row.
 *
 * PURE functions over authoritative anomaly_cases_v3 fields ONLY. They NEVER touch
 * anomaly_v1 scores/levels/reasons or Track A. Semantics kept separate:
 *   • anomaly_v1 reasons  → anomaly-reasons.ts (genuine codes only)
 *   • Track A ML          → mlScore/mlLevel (shown separately)
 *   • direct pair indicators (here) → FACTUAL comparisons, not model contributions
 *   • scenario rule check (here)    → factual rule verification, not anomaly explanation
 *   • recommendation (here)         → decision-support text, not a legal conclusion
 *
 * Missing/ambiguous fields → 'unknown' (—), never a fabricated true/false.
 */
import type { AnomalyCaseRow, AnomalyLevel, MlLevel } from './anomaly-cases-v3.types'
import { anomalyWeightSemantics } from './anomaly-semantics'
import { formatWeightDifference } from './weight-format'
import type { TranslateFn } from '@/lib/i18n'

export type TriState = 'match' | 'mismatch' | 'unknown'

export interface PairIndicator {
  key: string
  label: string
  state: TriState
  detail: string | null
}

// ── helpers ─────────────────────────────────────────────────────────────────
function norm(s: string | null | undefined): string | null {
  if (s == null) return null
  const t = String(s).trim()
  if (!t || ['nan', 'none', 'null', '-', '0'].includes(t.toLowerCase())) return null
  return t.toUpperCase()
}
function triBool(b: boolean | null | undefined): TriState {
  return b === true ? 'match' : b === false ? 'mismatch' : 'unknown'
}
function isFinite2(n: number | null | undefined): n is number {
  return typeof n === 'number' && Number.isFinite(n)
}
function stationsTri(r: AnomalyCaseRow): TriState {
  const a = norm(r.importStationTo)
  const b = norm(r.exportStationFrom)
  return a && b ? (a === b ? 'match' : 'mismatch') : 'unknown'
}

/**
 * SINGLE authoritative cargo-code equality result — used identically by the pair
 * indicator AND the scenario rule check. Derived ONLY from the pipeline flag
 * `cargoCodeChanged` (candidate_pairs.cargo_code_changed = NOT(same_etsng OR same_gng)),
 * which compares BOTH the import and export codes. Equality is NEVER inferred from
 * the import ЕТСНГ code alone. null → 'unknown' (never counts as a match).
 */
export const CARGO_MATCH_SOURCE_FIELD =
  'cargoCodeChanged (candidate_pairs.cargo_code_changed = НЕ(same_etsng ИЛИ same_gng))'
export function cargoCodeTri(r: AnomalyCaseRow): TriState {
  return r.cargoCodeChanged === false ? 'match' : r.cargoCodeChanged === true ? 'mismatch' : 'unknown'
}
/**
 * Scenario B wording: the export code is NOT carried in v3, but an authoritative
 * pair-level equality flag exists — so we state the pair-level verdict, we do not
 * assert two concrete codes are equal.
 */
export function cargoCodeDetail(state: TriState, t: TranslateFn): string {
  if (state === 'match') return t('analytics.caseAnalysis.cargoCode.match')
  if (state === 'mismatch') return t('analytics.caseAnalysis.cargoCode.mismatch')
  return t('analytics.caseAnalysis.cargoCode.unknown')
}
export function isHighAnomaly(l: AnomalyLevel): boolean {
  return l === 'priority_review' || l === 'high'
}
export function isHighMl(l: MlLevel): boolean {
  return l === 'high'
}

// ── §3 direct pair indicators (tri-state, authoritative only) ────────────────
export function buildPairIndicators(r: AnomalyCaseRow, t: TranslateFn): PairIndicator[] {
  const out: PairIndicator[] = []
  out.push({
    key: 'wagon', label: t('analytics.caseAnalysis.ind.wagon'), state: triBool(r.sameWagon),
    detail: r.wagon ? t('analytics.caseAnalysis.detail.wagon', { value: r.wagon }) : r.container ? t('analytics.caseAnalysis.detail.container', { value: r.container }) : t('analytics.caseAnalysis.noData'),
  })
  // «Совпадает груз» ТОЛЬКО из авторитетного флага cargoCodeChanged (сравнение ОБОИХ
  // кодов пары), НЕ из одного импортного поля. Экспортный код в v3 не хранится, поэтому
  // показываем вердикт уровня пары (сценарий B), а не «импортный код = X ✓». null → «—».
  out.push({
    key: 'cargo', label: t('analytics.caseAnalysis.ind.cargo'),
    state: cargoCodeTri(r),
    detail: cargoCodeDetail(cargoCodeTri(r), t),
  })
  out.push({
    key: 'station', label: t('analytics.caseAnalysis.ind.station'), state: stationsTri(r),
    detail: norm(r.importStationTo) && norm(r.exportStationFrom)
      ? `${r.importStationTo} / ${r.exportStationFrom}` : t('analytics.caseAnalysis.noData'),
  })
  out.push({
    key: 'entity', label: t('analytics.caseAnalysis.ind.entity'),
    state: triBool(r.importReceiverEqualsExportSender),
    detail: r.importReceiverEqualsExportSender == null ? t('analytics.caseAnalysis.noData')
      : t('analytics.caseAnalysis.crit.binPair', { import: r.importReceiverBin ?? '—', export: r.exportSenderBin ?? '—' }),
  })
  out.push({
    key: 'payer', label: t('analytics.caseAnalysis.ind.payer'), state: triBool(r.samePayerCode),
    detail: r.samePayerCode == null ? t('analytics.caseAnalysis.noData') : `${r.importPayerCode ?? '—'} / ${r.exportPayerCode ?? '—'}`,
  })
  out.push({
    key: 'days', label: t('analytics.caseAnalysis.ind.days'),
    state: isFinite2(r.daysBetween) ? (r.daysBetween <= 30 ? 'match' : 'mismatch') : 'unknown',
    detail: isFinite2(r.daysBetween) ? t('analytics.caseAnalysis.detail.days', { value: r.daysBetween }) : t('analytics.caseAnalysis.noData'),
  })
  out.push({
    key: 'weight', label: t('analytics.caseAnalysis.ind.weight'),
    state: isFinite2(r.weightDiffPct) ? (Math.abs(r.weightDiffPct) <= 10 ? 'match' : 'mismatch') : 'unknown',
    detail: isFinite2(r.weightDiffPct) ? `${r.weightDiffPct.toFixed(1)}%` : t('analytics.caseAnalysis.noData'),
  })
  return out
}

// ── §4 factors that weaken the pair connection (mismatches only, factual) ────
const WEAKEN_KEY: Record<string, string> = {
  wagon: 'analytics.caseAnalysis.weaken.wagon',
  cargo: 'analytics.caseAnalysis.weaken.cargo',
  station: 'analytics.caseAnalysis.weaken.station',
  entity: 'analytics.caseAnalysis.weaken.entity',
  payer: 'analytics.caseAnalysis.weaken.payer',
  days: 'analytics.caseAnalysis.weaken.days',
  weight: 'analytics.caseAnalysis.weaken.weight',
}
export function buildWeakeningFactors(r: AnomalyCaseRow, t: TranslateFn): string[] {
  const out: string[] = []
  for (const ind of buildPairIndicators(r, t)) {
    if (ind.state === 'mismatch' && WEAKEN_KEY[ind.key]) out.push(t(WEAKEN_KEY[ind.key]))
  }
  if (r.importWeight == null || r.exportWeight == null) {
    out.push(t('analytics.caseAnalysis.weaken.weightPartial'))
  }
  return out
}

// ── §2 combined case summary (deterministic matrix A–D) ──────────────────────
export function buildCaseSummary(r: AnomalyCaseRow, t: TranslateFn): string {
  const ha = isHighAnomaly(r.anomalyLevel)
  const hm = isHighMl(r.mlLevel)
  let core: string
  if (ha && hm) core = t('analytics.caseAnalysis.summary.a')
  else if (ha && !hm) core = t('analytics.caseAnalysis.summary.b')
  else if (!ha && hm) core = t('analytics.caseAnalysis.summary.c')
  else core = t('analytics.caseAnalysis.summary.d')
  return `${core} ${t('analytics.caseAnalysis.summary.note')}`
}

// ── §5 ML vs anomaly quadrant ────────────────────────────────────────────────
export function buildMatrix(r: AnomalyCaseRow, t: TranslateFn): { quadrant: 'A' | 'B' | 'C' | 'D'; text: string } {
  const ha = isHighAnomaly(r.anomalyLevel)
  const hm = isHighMl(r.mlLevel)
  if (ha && hm) return { quadrant: 'A', text: t('analytics.caseAnalysis.matrix.a') }
  if (ha && !hm) return { quadrant: 'B', text: t('analytics.caseAnalysis.matrix.b') }
  if (!ha && hm) return { quadrant: 'C', text: t('analytics.caseAnalysis.matrix.c') }
  return { quadrant: 'D', text: t('analytics.caseAnalysis.matrix.d') }
}

// ── §10 final recommendation (decision support) ──────────────────────────────
export function buildRecommendation(r: AnomalyCaseRow, t: TranslateFn): string {
  const ha = isHighAnomaly(r.anomalyLevel)
  const hm = isHighMl(r.mlLevel)
  if (ha && hm) return t('analytics.caseAnalysis.rec.a')
  if (ha && !hm) return t('analytics.caseAnalysis.rec.b')
  if (!ha && hm) return t('analytics.caseAnalysis.rec.c')
  return t('analytics.caseAnalysis.rec.d')
}

// ── §6 transport-leg connection (v3 facts only; border → null → «—») ─────────
export interface TransportLegs {
  importLeg: { country: string | null; shipment: string | null; date: string | null; kzArrival: string | null; border: string | null }
  internal: { importDest: string | null; exportOrigin: string | null; match: TriState; days: number | null }
  exportLeg: { country: string | null; shipment: string | null; date: string | null; kzDeparture: string | null; border: string | null }
}
export function buildTransportLegs(r: AnomalyCaseRow): TransportLegs {
  return {
    importLeg: { country: r.importCountryFrom, shipment: r.importShipmentId, date: r.importDate, kzArrival: r.importStationTo, border: null },
    internal: { importDest: r.importStationTo, exportOrigin: r.exportStationFrom, match: stationsTri(r), days: r.daysBetween },
    exportLeg: { country: r.exportCountryTo, shipment: r.exportShipmentId, date: r.exportDate, kzDeparture: r.exportStationFrom, border: null },
  }
}

// ── §8 factual scenario rule check (does NOT change anomaly_v1 / Track A) ─────
const SCENARIO_RULES: Record<number, string[]> = {
  1: ['wagon', 'cargo', 'station', 'bin', 'payer', 'weight', 'date'],
  2: ['wagon', 'cargo', 'station', 'weight', 'date'],
  3: ['wagon', 'cargo', 'bin', 'payer', 'weight', 'date'],
  4: ['wagon', 'cargo', 'weight', 'date'],
  5: ['cargo', 'station', 'bin', 'payer', 'weight', 'date'],
}
const RULE_LABEL_KEY: Record<string, string> = {
  wagon: 'analytics.caseAnalysis.rule.wagon',
  cargo: 'analytics.caseAnalysis.rule.cargo',
  station: 'analytics.caseAnalysis.rule.station',
  bin: 'analytics.caseAnalysis.rule.bin',
  payer: 'analytics.caseAnalysis.rule.payer',
  weight: 'analytics.caseAnalysis.rule.weight',
  date: 'analytics.caseAnalysis.rule.date',
}
function ruleState(key: string, r: AnomalyCaseRow): TriState {
  switch (key) {
    case 'wagon': return triBool(r.sameWagon)
    case 'cargo': return cargoCodeTri(r)
    case 'station': return stationsTri(r)
    case 'bin': return triBool(r.importReceiverEqualsExportSender)
    case 'payer': return triBool(r.samePayerCode)
    case 'weight': return isFinite2(r.weightDiffPct) ? (Math.abs(r.weightDiffPct) <= 10 ? 'match' : 'mismatch') : 'unknown'
    case 'date': return isFinite2(r.daysBetween) ? (r.daysBetween <= 30 ? 'match' : 'mismatch') : 'unknown'
    default: return 'unknown'
  }
}
export interface ScenarioRuleCheck {
  scenarioId: number
  required: { key: string; label: string; state: TriState }[]
  notRequired: { key: string; label: string; state: TriState }[]
  /** Tri-state breakdown over REQUIRED features. Unknown is reported separately and NEVER counted as matched. */
  matchedCount: number
  unmatchedCount: number
  unknownCount: number
  requiredCount: number
}
// ── Merged linkage view: required scenario criteria + optional facts ─────────
// Replaces the old pair-indicator / scenario-check split. The required list is
// generated from SCENARIO_RULES for the row's own scenario (1–5) — scenario 4 is
// not special-cased. Everything outside that list is context, so it is phrased
// neutrally and never marked as a failed check: "stations differ" is not a defect
// when the scenario never required them to match.

export interface LinkageCriterion {
  key: string
  /** Statement with the concrete value, e.g. «Один вагон: 52866548». */
  label: string
  state: TriState
  /** Threshold or source note, shown small. */
  detail: string | null
}
export interface Linkage {
  scenarioId: number
  required: LinkageCriterion[]
  optional: LinkageCriterion[]
  matchedCount: number
  unmatchedCount: number
  unknownCount: number
  requiredCount: number
}

const OPTIONAL_NOT_REQUIRED_KEY: Record<string, string> = {
  wagon: 'analytics.caseAnalysis.optional.wagon',
  cargo: 'analytics.caseAnalysis.optional.cargo',
  station: 'analytics.caseAnalysis.optional.station',
  bin: 'analytics.caseAnalysis.optional.bin',
  payer: 'analytics.caseAnalysis.optional.payer',
  weight: 'analytics.caseAnalysis.optional.weight',
  date: 'analytics.caseAnalysis.optional.date',
}

/** Required criterion phrased with the actual value from the row. */
function requiredCriterion(key: string, r: AnomalyCaseRow, t: TranslateFn): LinkageCriterion {
  const state = ruleState(key, r)
  switch (key) {
    case 'wagon': {
      const unit = r.wagon || r.container
      return {
        key, state,
        label: state === 'match' && unit ? t('analytics.caseAnalysis.crit.oneWagonValue', { value: unit }) : t('analytics.caseAnalysis.crit.oneWagon'),
        detail: state === 'unknown' ? t('analytics.caseAnalysis.noData') : null,
      }
    }
    case 'cargo': {
      const code = r.cargoGng ? t('analytics.caseAnalysis.crit.cargoGng', { code: r.cargoGng }) : r.cargoEtsng ? t('analytics.caseAnalysis.crit.cargoEtsng', { code: r.cargoEtsng }) : null
      return {
        key, state,
        label: state === 'match' && code ? t('analytics.caseAnalysis.crit.oneCargoValue', { value: code }) : t('analytics.caseAnalysis.crit.oneCargo'),
        detail: cargoCodeDetail(state, t),
      }
    }
    case 'date': {
      const d = isFinite2(r.daysBetween) ? r.daysBetween : null
      return {
        key, state,
        label: d != null ? t('analytics.caseAnalysis.crit.intervalValue', { days: d }) : t('analytics.caseAnalysis.crit.interval'),
        detail: t('analytics.caseAnalysis.crit.intervalNote'),
      }
    }
    case 'weight': {
      // Same formatter as the table and the drawer: a difference that merely
      // rounds to zero must not read here as «Разница веса 0.0%» either.
      // Когда разницу посчитать нельзя, признак называет ПРИЧИНУ: «нет данных» и
      // «указан 0 т» — разные состояния, и ни одно из них не равно порожности.
      // Состояние признака (`state`) при этом не меняется: unknown остаётся unknown.
      const w = formatWeightDifference(r)
      const sem = anomalyWeightSemantics(r)
      return {
        key, state,
        label: w.status === 'exact_match' ? t('analytics.caseAnalysis.crit.weightMatch')
          : w.exactPercent != null ? t('analytics.caseAnalysis.crit.weightDiff', { value: w.displayValue })
          : t('analytics.caseAnalysis.crit.weightCompare', { value: sem.comparisonText }),
        detail: w.exactPercent != null ? t('analytics.caseAnalysis.crit.weightNote') : sem.tooltip,
      }
    }
    case 'station': {
      const a = r.importStationTo
      const b = r.exportStationFrom
      return {
        key, state,
        label: state === 'match' && a ? t('analytics.caseAnalysis.crit.oneStation', { value: a }) : t('analytics.caseAnalysis.crit.stationChain'),
        detail: state === 'mismatch' && a && b ? `${a} → ${b}` : state === 'unknown' ? t('analytics.caseAnalysis.noData') : null,
      }
    }
    case 'bin':
      return {
        key, state,
        label: t('analytics.caseAnalysis.crit.entityChain'),
        detail: state === 'mismatch' && (r.importReceiverBin || r.exportSenderBin)
          ? `${r.importReceiverBin ?? '—'} / ${r.exportSenderBin ?? '—'}`
          : state === 'unknown' ? t('analytics.caseAnalysis.noData') : null,
      }
    case 'payer':
      return {
        key, state,
        label: t('analytics.caseAnalysis.crit.onePayer'),
        detail: state === 'mismatch' && (r.importPayerCode || r.exportPayerCode)
          ? `${r.importPayerCode ?? '—'} / ${r.exportPayerCode ?? '—'}`
          : state === 'unknown' ? t('analytics.caseAnalysis.noData') : null,
      }
    default:
      return { key, state, label: RULE_LABEL_KEY[key] ? t(RULE_LABEL_KEY[key]) : key, detail: null }
  }
}

/**
 * Optional fact — reported as information, never as a failed requirement.
 * A differing station renders as «Станции различаются: A → B», with the note
 * that scenario N does not require them to match.
 */
function optionalCriterion(key: string, r: AnomalyCaseRow, scenarioId: number, t: TranslateFn): LinkageCriterion | null {
  const state = ruleState(key, r)
  if (state === 'unknown') return null
  const base = OPTIONAL_NOT_REQUIRED_KEY[key]
    ? t(OPTIONAL_NOT_REQUIRED_KEY[key])
    : t('analytics.caseAnalysis.optional.generic')
  const note = t('analytics.caseAnalysis.optional.scenarioSuffix', { base, scenario: scenarioId })
  switch (key) {
    case 'station': {
      const a = r.importStationTo
      const b = r.exportStationFrom
      if (state === 'match') return { key, state, label: a ? t('analytics.caseAnalysis.crit.oneStation', { value: a }) : t('analytics.caseAnalysis.crit.stationsMatch'), detail: note }
      return { key, state, label: a && b ? t('analytics.caseAnalysis.crit.stationsDiffValue', { from: a, to: b }) : t('analytics.caseAnalysis.crit.stationsDiff'), detail: note }
    }
    case 'bin':
      return {
        key, state,
        label: state === 'match' ? t('analytics.caseAnalysis.crit.entityChain') : t('analytics.caseAnalysis.crit.entityDiff'),
        detail: note,
      }
    case 'payer':
      return {
        key, state,
        label: state === 'match' ? t('analytics.caseAnalysis.crit.onePayer') : t('analytics.caseAnalysis.crit.payerDiff'),
        detail: note,
      }
    case 'wagon':
      return { key, state, label: state === 'match' ? t('analytics.caseAnalysis.crit.oneWagonValue', { value: r.wagon || r.container || '—' }) : t('analytics.caseAnalysis.crit.diffWagon'), detail: note }
    case 'cargo':
      return { key, state, label: state === 'match' ? t('analytics.caseAnalysis.crit.cargoMatch') : t('analytics.caseAnalysis.crit.cargoDiff'), detail: note }
    case 'weight': {
      const w = formatWeightDifference(r)
      if (w.exactPercent == null) return null
      return {
        key, state,
        label: w.status === 'exact_match' ? t('analytics.caseAnalysis.crit.weightMatch') : t('analytics.caseAnalysis.crit.weightDiff', { value: w.displayValue }),
        detail: note,
      }
    }
    case 'date': {
      const d = isFinite2(r.daysBetween) ? r.daysBetween : null
      return d == null ? null : { key, state, label: t('analytics.caseAnalysis.crit.intervalValue', { days: d }), detail: note }
    }
    default:
      return null
  }
}

export function buildLinkage(r: AnomalyCaseRow, t: TranslateFn): Linkage {
  const requiredKeys = SCENARIO_RULES[r.scenarioId] ?? []
  const allKeys = ['wagon', 'cargo', 'station', 'bin', 'payer', 'weight', 'date']
  const required = requiredKeys.map((k) => requiredCriterion(k, r, t))
  const optional = allKeys
    .filter((k) => !requiredKeys.includes(k))
    .map((k) => optionalCriterion(k, r, r.scenarioId, t))
    .filter((x): x is LinkageCriterion => x !== null)
  return {
    scenarioId: r.scenarioId,
    required,
    optional,
    matchedCount: required.filter((x) => x.state === 'match').length,
    unmatchedCount: required.filter((x) => x.state === 'mismatch').length,
    unknownCount: required.filter((x) => x.state === 'unknown').length,
    requiredCount: required.length,
  }
}

export function buildScenarioRuleCheck(r: AnomalyCaseRow, t: TranslateFn): ScenarioRuleCheck {
  const required = SCENARIO_RULES[r.scenarioId] ?? []
  const allKeys = ['wagon', 'cargo', 'station', 'bin', 'payer', 'weight', 'date']
  const req = required.map((k) => ({ key: k, label: t(RULE_LABEL_KEY[k]), state: ruleState(k, r) }))
  const notReq = allKeys.filter((k) => !required.includes(k)).map((k) => ({ key: k, label: t(RULE_LABEL_KEY[k]), state: ruleState(k, r) }))
  return {
    scenarioId: r.scenarioId,
    required: req,
    notRequired: notReq,
    matchedCount: req.filter((x) => x.state === 'match').length,
    unmatchedCount: req.filter((x) => x.state === 'mismatch').length,
    unknownCount: req.filter((x) => x.state === 'unknown').length,
    requiredCount: req.length,
  }
}

// ── §9 analyst checklist (from genuine reasons + direct facts) ────────────────
/*
 * Stage 39: the per-reason checklist templates were removed.
 *
 * They restated a model reason in words («Проверить, почему для этого вагона/контейнера
 * впервые встречается такой переход груза») with no number in them, and they spoke the
 * model's vocabulary. The reason cards now carry evidence tables, and
 * `buildEvidenceChecklist` derives the checklist line from the SAME numbers — so a line
 * here would either duplicate it or contradict it. What remains below is the factual part:
 * items about the pair itself, which no reason code covers.
 */

export function buildChecklist(r: AnomalyCaseRow, t: TranslateFn, _supportedCodes: string[] = []): string[] {
  const items: string[] = []
  if (isFinite2(r.weightDiffPct) && Math.abs(r.weightDiffPct) <= 10) {
    items.push(t('analytics.caseAnalysis.check.weight'))
  }
  if (stationsTri(r) === 'mismatch') {
    items.push(t('analytics.caseAnalysis.check.station'))
  }
  if (r.importReceiverEqualsExportSender === true) {
    items.push(t('analytics.caseAnalysis.check.entity'))
  }
  if (r.samePayerCode === true) items.push(t('analytics.caseAnalysis.check.payer'))
  if (isFinite2(r.daysBetween) && r.daysBetween <= 30) items.push(t('analytics.caseAnalysis.check.days'))
  if (r.importWeight == null || r.exportWeight == null) items.push(t('analytics.caseAnalysis.check.weightMissing'))
  return items
}

// ── §12 richer empty-reason explanation ──────────────────────────────────────
export function buildEmptyReasonExplanation(r: AnomalyCaseRow, t: TranslateFn): string {
  const noHistory = r.anomalyCohort === 'same_unit_no_history_wagon'
  const group = noHistory ? t('analytics.caseAnalysis.dist.groupNoHistory') : t('analytics.caseAnalysis.dist.groupWithHistory')
  const pct = isFinite2(r.anomalyPercentile)
    ? t('analytics.caseAnalysis.dist.percentileValue', { value: r.anomalyPercentile.toFixed(1) })
    : t('analytics.caseAnalysis.dist.percentileHigh')
  let out = t('analytics.caseAnalysis.dist.lead', { percentile: pct, group })
  if (noHistory) out += t('analytics.caseAnalysis.dist.noHistoryNote')
  out += t('analytics.caseAnalysis.dist.tail')
  return out
}
